import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './users-page.css';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import UsersTable, { COLUMN_DEFS } from './components/UsersTable.jsx';
import UserGroupsTable, { GROUP_COLUMN_DEFS } from './components/UserGroupsTable.jsx';
import UserDrawer from './components/UserDrawer.jsx';
import UserGroupDrawer from './components/UserGroupDrawer.jsx';
import ResetPasswordDialog from './components/ResetPasswordDialog.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../community/residents/components/ActiveFilterBar.jsx';
import ColumnVisibilityToggle from '../community/residents/components/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../community/residents/components/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, setSession, getCampusId, getUserId, getSessionId,
  getCampusList, getRoleWeight, getCampusConfig, updateSessionCampus,
} from '../../services/session.js';
import {
  fetchUsers, fetchRoles, fetchUserGroupList,
  addUser, editUser, deleteUsers, resetPassword,
} from './api/usersApi.js';
import {
  fetchUserGroups, addUserGroup, editUserGroup, deleteUserGroups,
} from './api/userGroupsApi.js';
import { exportUsersCsv, exportUserGroupsCsv } from '../../services/csvExport.js';
import { iAmHigherAuthority as iAmHigherAuth } from '../../libs.jsx';

/** Default visible columns for each tab */
const DEFAULT_USER_VISIBLE = [
  'username',
  'display_name',
  'email',
  'phone',
  'title',
  'user_role_name',
  'group_name',
];

const DEFAULT_GROUP_VISIBLE = [
  'group_name',
  'capcode',
  'user_count',
];

/**
 * UsersPage — main orchestrator for the /users V2 redesign.
 *
 * Manages: real API data loading for Users and User Groups tabs,
 * search, column filters, sort, pagination, row selection, drawers,
 * tab switching, and role-weight authorization.
 */
const UsersPage = ({ initialTab = 'users' }) => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);
  const myRoleWeight = getRoleWeight();
  const myUsername = useMemo(() => getSession()?.username || '', []);
  const displayEditAllowed = useMemo(() => !!getSession()?.displayEdit, []);
  const isCorp = useMemo(() => getSession()?.corp === 1, []);

  // ---------- Data & Loading ----------
  const [usersData, setUsersData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [groupListData, setGroupListData] = useState([]);
  const [userGroupsData, setUserGroupsData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);
  const firstLoad = useRef(true);

  // ---------- Tab ----------
  const activeTab = initialTab;

  // ---------- Page-level Toast ----------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // USERS TAB STATE
  // ==========================================================================
  const [userSearch, setUserSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [userSortColumn, setUserSortColumn] = useState('username');
  const [userSortOrder, setUserSortOrder] = useState('asc');
  const [userPage, setUserPage] = useState(1);
  const [userPerPage, setUserPerPage] = useState(25);
  const [userVisibleColumns, setUserVisibleColumns] = useState(DEFAULT_USER_VISIBLE);
  const [userSelectedIds, setUserSelectedIds] = useState([]);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  // ==========================================================================
  // USER GROUPS TAB STATE
  // ==========================================================================
  const [groupSearch, setGroupSearch] = useState('');
  const [groupFilters, setGroupFilters] = useState({});
  const [groupSortColumn, setGroupSortColumn] = useState('group_name');
  const [groupSortOrder, setGroupSortOrder] = useState('asc');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPerPage, setGroupPerPage] = useState(25);
  const [groupVisibleColumns, setGroupVisibleColumns] = useState(DEFAULT_GROUP_VISIBLE);
  const [groupSelectedIds, setGroupSelectedIds] = useState([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // ---------- Helper: get session params for API write calls ----------
  const getSessionParams = useCallback(() => {
    const session = getSession();
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      username: session?.username || '',
    };
  }, [campusId]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  /** Load users + roles + group list (for user add/edit form) */
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const isFirstLoad = firstLoad.current;
      const [uData, rData, gData] = await Promise.all([
        fetchUsers(campusId, !isFirstLoad),
        fetchRoles(),
        fetchUserGroupList(campusId),
      ]);
      firstLoad.current = false;
      setUsersData(uData || []);
      setRoleData(rData || []);
      setGroupListData(gData || []);
    } catch (err) {
      console.error('[UsersPage] Failed to load users data:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [campusId]);

  /** Load user groups (with their user members) */
  const loadUserGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const data = await fetchUserGroups(campusId);
      setUserGroupsData(data || []);
    } catch (err) {
      console.error('[UsersPage] Failed to load user groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [campusId]);

  // Initial load
  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadUserGroups(); }, [loadUserGroups]);

  // ==========================================================================
  // USERS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const filteredUsers = useMemo(() => {
    let data = [...usersData];

    // 1. Search
    if (userSearch.trim()) {
      const s = userSearch.toLowerCase();
      data = data.filter(
        (r) =>
          r.username?.toLowerCase().includes(s) ||
          r.display_name?.toLowerCase().includes(s) ||
          r.email?.toLowerCase().includes(s) ||
          r.user_role_name?.toLowerCase().includes(s) ||
          r.title?.toLowerCase().includes(s) ||
          r.phone?.toLowerCase().includes(s) ||
          r.group_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(userFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        if (columnKey === 'group_name') {
          const gName = row.group_name || '';
          if (values.includes('(none)') && !gName) return true;
          // group_name is comma-separated
          const names = gName.split(',').map((n) => n.trim()).filter(Boolean);
          return names.some((n) => values.includes(n)) || (values.includes('(none)') && names.length === 0);
        }
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal = a[userSortColumn];
      let bVal = b[userSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return userSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [usersData, userSearch, userFilters, userSortColumn, userSortOrder]);

  // Users Pagination
  const userTotalFiltered = filteredUsers.length;
  const userTotalPages = Math.max(1, Math.ceil(userTotalFiltered / userPerPage));
  const userSafePage = Math.min(userPage, userTotalPages);

  const paginatedUsers = useMemo(() => {
    const start = (userSafePage - 1) * userPerPage;
    return filteredUsers.slice(start, start + userPerPage);
  }, [filteredUsers, userSafePage, userPerPage]);

  const userShowingStart = userTotalFiltered === 0 ? 0 : (userSafePage - 1) * userPerPage + 1;
  const userShowingEnd = Math.min(userSafePage * userPerPage, userTotalFiltered);

  useEffect(() => { setUserPage(1); }, [userSearch, userFilters, userPerPage]);

  const userPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, userSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(userTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [userSafePage, userTotalPages]);

  // Users handlers
  const handleUserSort = useCallback((column) => {
    if (userSortColumn === column) {
      setUserSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortColumn(column);
      setUserSortOrder('asc');
    }
  }, [userSortColumn]);

  const handleUserFilterChange = useCallback((columnKey, values) => {
    setUserFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleUserRemoveFilter = useCallback((columnKey, value) => {
    setUserFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleUserClearAllFilters = useCallback(() => {
    setUserFilters({});
    setUserSearch('');
  }, []);

  const handleUserSelectRow = useCallback((id) => {
    setUserSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  /** Select-all respects role_weight: only selects users the current user has authority over */
  const handleUserSelectAll = useCallback(() => {
    const manageableIds = paginatedUsers
      .filter((u) => iAmHigherAuth(u.role_weight, myRoleWeight))
      .map((u) => u.user_id);
    const allSelected = manageableIds.length > 0 && manageableIds.every((id) => userSelectedIds.includes(id));
    if (allSelected) {
      setUserSelectedIds((prev) => prev.filter((id) => !manageableIds.includes(id)));
    } else {
      setUserSelectedIds((prev) => [...new Set([...prev, ...manageableIds])]);
    }
  }, [paginatedUsers, userSelectedIds, myRoleWeight]);

  const handleUserColumnToggle = useCallback((columnKey) => {
    setUserVisibleColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey]
    );
  }, []);

  // User drawer handlers
  const openAddUser = useCallback(() => {
    setEditingUser(null);
    setUserDrawerOpen(true);
  }, []);

  const openEditUser = useCallback((user) => {
    // Block editing users with equal/higher weight (unless PalCare Admin or self)
    if (!iAmHigherAuth(user.role_weight, myRoleWeight) && user.username !== myUsername) {
      showToast('error', `You do not have permission to edit ${user.display_name || user.username}.`);
      return;
    }
    setEditingUser(user);
    setUserDrawerOpen(true);
  }, [myRoleWeight, myUsername, showToast]);

  const handleSaveUser = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      if (formData.user_id) {
        // Edit mode — build oldData from current users array
        const original = usersData.find((u) => u.user_id === formData.user_id);
        const oldData = {
          user_id: original?.user_id,
          display_name: original?.display_name,
          email: original?.email,
          user_role_id: original?.user_role_id,
          title: original?.title,
          group_ids: original?.group_ids || [],
          username: original?.username,
          cognito_id: original?.cognito_id,
          phone: original?.phone,
        };
        result = await editUser(formData, oldData, session);

        // If editing own profile, update session storage
        if (result[0] && original?.username === myUsername) {
          const s = getSession();
          if (s) {
            s.email = formData.email;
            s.display = formData.display_name;
            s.groups = formData.group_ids;
            setSession(s);
          }
        }
      } else {
        // Add mode
        result = await addUser(formData, session);
      }

      if (result[0]) {
        setUserDrawerOpen(false);
        loadUsers();
        loadUserGroups(); // Users display group names
        showToast('success', formData.user_id
          ? `${formData.display_name} updated`
          : `${formData.display_name || formData.username} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save user');
      }
    },
    [usersData, getSessionParams, loadUsers, loadUserGroups, showToast, myUsername]
  );

  const handleDeleteUser = useCallback(
    async (user) => {
      const session = getSessionParams();
      const userName = user.display_name || user.username;

      if (!iAmHigherAuth(user.role_weight, myRoleWeight)) {
        showToast('error', `You do not have permission to delete ${userName}.`);
        return;
      }

      const result = await deleteUsers([{
        user_id: user.user_id,
        username: user.username,
        cognito_id: user.cognito_id,
        user_role_id: user.user_role_id,
      }], session);

      if (result[0]) {
        setUserSelectedIds((prev) => prev.filter((id) => id !== user.user_id));
        setUserDrawerOpen(false);
        loadUsers();
        loadUserGroups();
        showToast('success', `${userName} deleted`);
      } else {
        showToast('error', result[1] || 'Delete failed');
      }
    },
    [getSessionParams, loadUsers, loadUserGroups, showToast, myRoleWeight]
  );

  const handleBulkDeleteUsers = useCallback(async () => {
    // Filter to only manageable users
    const manageable = [];
    const denied = [];

    userSelectedIds.forEach((id) => {
      const user = usersData.find((u) => u.user_id === id);
      if (user) {
        if (iAmHigherAuth(user.role_weight, myRoleWeight)) {
          manageable.push(user);
        } else {
          denied.push(user);
        }
      }
    });

    if (manageable.length === 0) {
      showToast('error', 'You do not have permission to delete any of the selected users.');
      return;
    }

    if (denied.length > 0) {
      const deniedNames = denied.map((u) => u.display_name || u.username).join(', ');
      showToast('error', `Cannot delete: ${deniedNames} (insufficient permissions). Processing remaining users.`);
    }

    if (!confirm(`Delete ${manageable.length} user(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const count = manageable.length;

    const payload = manageable.map((u) => ({
      user_id: u.user_id,
      username: u.username,
      cognito_id: u.cognito_id,
      user_role_id: u.user_role_id,
    }));

    const result = await deleteUsers(payload, session);
    if (result[0]) {
      setUserSelectedIds([]);
      loadUsers();
      loadUserGroups();
      showToast('success', `${count} user${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [userSelectedIds, usersData, myRoleWeight, getSessionParams, loadUsers, loadUserGroups, showToast]);

  // Opens the Reset Password dialog from row action or drawer
  const openResetPasswordDialog = useCallback((user) => {
    setResetPasswordUser(user);
  }, []);

  // Called by ResetPasswordDialog after the user enters a new password
  const handleResetPassword = useCallback(
    async (data) => {
      const session = getSessionParams();
      const result = await resetPassword(data, session);
      if (result[0]) {
        setResetPasswordUser(null);
        showToast('success', `Password reset for ${data.username}`);
      }
      return result;
    },
    [getSessionParams, showToast]
  );

  // User exports
  const handleUserExportAll = useCallback(() => {
    if (filteredUsers.length) exportUsersCsv(filteredUsers, 'users.csv', myRoleWeight);
  }, [filteredUsers, myRoleWeight]);

  const handleUserBulkExport = useCallback(() => {
    const selected = usersData.filter((u) => userSelectedIds.includes(u.user_id));
    if (selected.length) exportUsersCsv(selected, 'users-selected.csv', myRoleWeight);
  }, [userSelectedIds, usersData, myRoleWeight]);

  // ==========================================================================
  // USER GROUPS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const enrichedGroups = useMemo(() => {
    return (userGroupsData || []).map((group) => ({
      ...group,
      user_count: (group.users || []).length,
    }));
  }, [userGroupsData]);

  const filteredGroups = useMemo(() => {
    let data = [...enrichedGroups];

    // 1. Search
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      data = data.filter(
        (g) =>
          g.group_name?.toLowerCase().includes(s) ||
          g.capcode?.toLowerCase().includes(s) ||
          (g.users || []).some((u) => u.display_name?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(groupFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (groupSortColumn === 'user_count') {
        aVal = (a.users || []).length;
        bVal = (b.users || []).length;
        const result = aVal - bVal;
        return groupSortOrder === 'asc' ? result : -result;
      }
      aVal = a[groupSortColumn];
      bVal = b[groupSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return groupSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedGroups, groupSearch, groupFilters, groupSortColumn, groupSortOrder]);

  // Groups Pagination
  const groupTotalFiltered = filteredGroups.length;
  const groupTotalPages = Math.max(1, Math.ceil(groupTotalFiltered / groupPerPage));
  const groupSafePage = Math.min(groupPage, groupTotalPages);

  const paginatedGroups = useMemo(() => {
    const start = (groupSafePage - 1) * groupPerPage;
    return filteredGroups.slice(start, start + groupPerPage);
  }, [filteredGroups, groupSafePage, groupPerPage]);

  const groupShowingStart = groupTotalFiltered === 0 ? 0 : (groupSafePage - 1) * groupPerPage + 1;
  const groupShowingEnd = Math.min(groupSafePage * groupPerPage, groupTotalFiltered);

  useEffect(() => { setGroupPage(1); }, [groupSearch, groupFilters, groupPerPage]);

  const groupPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, groupSafePage - Math.floor(maxButtons / 2));
    let end = Math.min(groupTotalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [groupSafePage, groupTotalPages]);

  // Group handlers
  const handleGroupSort = useCallback((column) => {
    if (groupSortColumn === column) {
      setGroupSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setGroupSortColumn(column);
      setGroupSortOrder('asc');
    }
  }, [groupSortColumn]);

  const handleGroupFilterChange = useCallback((columnKey, values) => {
    setGroupFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleGroupRemoveFilter = useCallback((columnKey, value) => {
    setGroupFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleGroupClearAllFilters = useCallback(() => {
    setGroupFilters({});
    setGroupSearch('');
  }, []);

  const handleGroupSelectRow = useCallback((id) => {
    setGroupSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleGroupSelectAll = useCallback(() => {
    const allIds = paginatedGroups.map((g) => g.group_id);
    const allSelected = allIds.every((id) => groupSelectedIds.includes(id));
    if (allSelected) {
      setGroupSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setGroupSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedGroups, groupSelectedIds]);

  const handleGroupColumnToggle = useCallback((columnKey) => {
    setGroupVisibleColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey]
    );
  }, []);

  // Group drawer handlers
  const openAddGroup = useCallback(() => {
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  }, []);

  const openEditGroup = useCallback((group) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Resolve cognito_ids from users data
      const cognitoIds = (formData.user_ids || []).map((uid) => {
        const u = usersData.find((usr) => usr.user_id === uid);
        return u?.cognito_id || '';
      }).filter(Boolean);

      if (formData.group_id) {
        // Edit mode
        const original = userGroupsData.find((g) => g.group_id === formData.group_id);
        const oldUserIds = (original?.users || []).map((u) => u.user_id);
        const newUserIds = formData.user_ids || [];

        // Build remove list (users being removed)
        const removeList = oldUserIds.filter((id) => !newUserIds.includes(id));

        const editData = {
          group_id: formData.group_id,
          group_name: formData.group_name,
          capcode: formData.capcode,
          user_ids: newUserIds,
          cognito_ids: cognitoIds,
          remove_list: removeList,
        };
        const oldData = {
          group_id: original?.group_id,
          group_name: original?.group_name,
          capcode: original?.capcode,
          user_ids: oldUserIds,
          campus_id: campusId,
        };
        result = await editUserGroup(editData, oldData, session);
      } else {
        // Add mode
        const addData = {
          group_name: formData.group_name,
          capcode: formData.capcode,
          user_ids: formData.user_ids || [],
          cognito_ids: cognitoIds,
        };
        result = await addUserGroup(addData, session);
      }

      if (result[0]) {
        setGroupDrawerOpen(false);
        loadUserGroups();
        loadUsers(); // Users display group names
        showToast('success', formData.group_id
          ? `${formData.group_name} updated`
          : `${formData.group_name} created`
        );
      } else {
        showToast('error', result[1] || 'Failed to save group');
      }
    },
    [usersData, userGroupsData, campusId, getSessionParams, loadUserGroups, loadUsers, showToast]
  );

  const handleDeleteGroup = useCallback(
    async (group) => {
      const session = getSessionParams();
      const groupName = group.group_name;

      // Build user_ids and cognito_ids from the group's users
      const userIds = (group.users || []).map((u) => u.user_id);
      const cognitoIds = (group.users || []).map((u) => u.cognito_id).filter(Boolean);

      const result = await deleteUserGroups([{
        group_id: group.group_id,
        user_ids: userIds,
        cognito_ids: cognitoIds,
      }], session);

      if (result[0]) {
        setGroupSelectedIds((prev) => prev.filter((id) => id !== group.group_id));
        setGroupDrawerOpen(false);
        loadUserGroups();
        loadUsers();
        showToast('success', `${groupName} deleted`);
      } else {
        showToast('error', result[1] || 'Delete failed');
      }
    },
    [getSessionParams, loadUserGroups, loadUsers, showToast]
  );

  const handleGroupBulkDelete = useCallback(async () => {
    if (!confirm(`Delete ${groupSelectedIds.length} group(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const count = groupSelectedIds.length;

    const payload = groupSelectedIds.map((gid) => {
      const group = userGroupsData.find((g) => g.group_id === gid);
      return {
        group_id: gid,
        user_ids: (group?.users || []).map((u) => u.user_id),
        cognito_ids: (group?.users || []).map((u) => u.cognito_id).filter(Boolean),
      };
    });

    const result = await deleteUserGroups(payload, session);
    if (result[0]) {
      setGroupSelectedIds([]);
      loadUserGroups();
      loadUsers();
      showToast('success', `${count} group${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [groupSelectedIds, userGroupsData, getSessionParams, loadUserGroups, loadUsers, showToast]);

  // Group exports
  const handleGroupExportAll = useCallback(() => {
    if (filteredGroups.length) exportUserGroupsCsv(filteredGroups, 'user-groups.csv', myRoleWeight);
  }, [filteredGroups, myRoleWeight]);

  const handleGroupBulkExport = useCallback(() => {
    const selected = enrichedGroups.filter((g) => groupSelectedIds.includes(g.group_id));
    if (selected.length) exportUserGroupsCsv(selected, 'user-groups-selected.csv', myRoleWeight);
  }, [groupSelectedIds, enrichedGroups, myRoleWeight]);

  // ==========================================================================
  // COLUMN LABEL MAPS (for ActiveFilterBar)
  // ==========================================================================
  const userColumnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );
  const groupColumnLabels = useMemo(
    () => Object.fromEntries(GROUP_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // Campus name
  const campusName = useMemo(() => {
    const session = getSession();
    if (!session) return '';
    const list = session.acntList || [];
    const found = list.find?.((c) => (c.campus_id || c.CampusId) === campusId);
    return found?.campus_name || found?.CampusName || session.campus || '';
  }, [campusId]);

  // ==========================================================================
  // Helper: Pagination component (reused across 2 tabs)
  // ==========================================================================
  const renderPagination = (prefix, showingStart, showingEnd, totalFiltered, entityLabel, safePage, totalPages, pageNumbers, setPageFn, perPageVal, setPerPageFn, perPageId) => (
    <div className="up-pagination">
      <p className="up-pagination__info">
        Showing <span className="up-pagination__emphasis">{showingStart}</span>&ndash;<span className="up-pagination__emphasis">{showingEnd}</span> of{' '}
        <span className="up-pagination__emphasis">{totalFiltered}</span> {entityLabel}
      </p>
      <div className="up-pagination__controls">
        <div className="up-per-page">
          <label htmlFor={perPageId} className="up-per-page__label">Rows per page:</label>
          <select
            id={perPageId}
            value={perPageVal}
            onChange={(e) => setPerPageFn(parseInt(e.target.value, 10))}
            className="up-per-page__select"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <nav className="up-page-nav" aria-label={`${entityLabel} pagination`}>
          <button
            type="button"
            onClick={() => setPageFn((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="up-page-btn"
            aria-label="Previous page"
          >
            <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          {pageNumbers.map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => setPageFn(num)}
              className={`up-page-num ${num === safePage ? 'is-active' : ''}`}
              aria-label={`Page ${num}`}
              aria-current={num === safePage ? 'page' : undefined}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setPageFn((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="up-page-btn"
            aria-label="Next page"
          >
            <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  );

  // ==========================================================================
  // Helper: Bulk action bar (reused across 2 tabs)
  // ==========================================================================
  const renderBulkBar = (selectedCount, entityLabel, onExport, onDelete, onClear) => {
    if (selectedCount === 0) return null;
    return (
      <div className="up-bulk-bar">
        <div className="up-bulk-bar__left">
          <span className="up-bulk-bar__count">
            {selectedCount} {entityLabel}{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={onClear} className="up-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="up-bulk-bar__right">
          <button type="button" onClick={onExport} className="up-bulk-btn" aria-label={`Export selected ${entityLabel}s`}>
            <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
          <button type="button" onClick={onDelete} className="up-bulk-btn up-bulk-btn--danger" aria-label={`Delete selected ${entityLabel}s`}>
            <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="up-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`up-toast ${toast.type === 'success' ? 'up-toast--success' : 'up-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="up-toast__close"
            aria-label="Dismiss"
          >
            <svg className="up-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="up-page-header">
        <div className="up-page-header__inner">
          <div className="up-breadcrumb-row">
            <div className="up-breadcrumb">
              <span className="up-breadcrumb__text">Management</span>
              <svg className="up-icon-md up-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="up-breadcrumb__current">Users</span>
            </div>
            <div className="up-campus-row">
              <label htmlFor="up-campus-selector" className="up-campus-label">Campus:</label>
              {campusList.length > 1 ? (
                <select
                  id="up-campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="up-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="up-campus-name">{campusName}</span>
              )}
            </div>
          </div>
          <h1 className="up-page-title">Users</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="up-tab-bar">
        <div className="up-tab-bar__inner">
          <nav className="up-tab-nav" aria-label="Tabs">
            <Link to="/users/usermanagement" className={`up-tab ${activeTab === 'users' ? 'is-active' : ''}`}>
              Users ({usersData.length})
              {activeTab === 'users' ? <span className="up-tab__indicator" /> : null}
            </Link>
            <Link to="/users/groups" className={`up-tab ${activeTab === 'usergroups' ? 'is-active' : ''}`}>
              User Groups ({enrichedGroups.length})
              {activeTab === 'usergroups' ? <span className="up-tab__indicator" /> : null}
            </Link>
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="up-content">
        {/* ========== USERS TAB ========== */}
        {activeTab === 'users' ? (
          <>
            <div className="up-controls">
              <div className="up-controls__left">
                <div className="up-search">
                  <svg className="up-icon-md up-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search users..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="up-search__input"
                    aria-label="Search users"
                  />
                </div>
              </div>
              <div className="up-controls__right">
                <ColumnVisibilityToggle columns={COLUMN_DEFS} visibleColumns={userVisibleColumns} onToggle={handleUserColumnToggle} />
                <button type="button" className="up-btn up-btn--secondary" onClick={handleUserExportAll} aria-label="Export users">
                  <svg className="up-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                <button type="button" onClick={openAddUser} className="up-btn up-btn--primary" aria-label="Add new user">
                  <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New User
                </button>
              </div>
            </div>
            <ActiveFilterBar filters={userFilters} columnLabels={userColumnLabels} onRemoveFilter={handleUserRemoveFilter} onClearAll={handleUserClearAllFilters} />
            {renderBulkBar(userSelectedIds.length, 'user', handleUserBulkExport, handleBulkDeleteUsers, () => setUserSelectedIds([]))}
            {usersLoading ? (
              <TableSkeleton />
            ) : (
              <UsersTable
                data={paginatedUsers}
                allData={usersData}
                visibleColumns={userVisibleColumns}
                sortColumn={userSortColumn}
                sortOrder={userSortOrder}
                onSort={handleUserSort}
                filters={userFilters}
                onFilterChange={handleUserFilterChange}
                selectedIds={userSelectedIds}
                onSelectRow={handleUserSelectRow}
                onSelectAll={handleUserSelectAll}
                onEditUser={openEditUser}
                onDeleteUser={handleDeleteUser}
                onResetPassword={openResetPasswordDialog}
                onAddUser={openAddUser}
                onClearFilters={handleUserClearAllFilters}
                myRoleWeight={myRoleWeight}
                iAmHigherAuthority={iAmHigherAuth}
                roleData={roleData}
              />
            )}
            {!usersLoading && userTotalFiltered > 0
              ? renderPagination('user', userShowingStart, userShowingEnd, userTotalFiltered, 'users', userSafePage, userTotalPages, userPageNumbers, setUserPage, userPerPage, setUserPerPage, 'user-per-page')
              : null}
          </>
        ) : (
          /* ========== USER GROUPS TAB ========== */
          <>
            <div className="up-controls">
              <div className="up-controls__left">
                <div className="up-search">
                  <svg className="up-icon-md up-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="up-search__input"
                    aria-label="Search user groups"
                  />
                </div>
              </div>
              <div className="up-controls__right">
                <ColumnVisibilityToggle columns={GROUP_COLUMN_DEFS} visibleColumns={groupVisibleColumns} onToggle={handleGroupColumnToggle} />
                <button type="button" className="up-btn up-btn--secondary" onClick={handleGroupExportAll} aria-label="Export groups">
                  <svg className="up-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>
                <button type="button" onClick={openAddGroup} className="up-btn up-btn--primary" aria-label="Add new group">
                  <svg className="up-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New Group
                </button>
              </div>
            </div>
            <ActiveFilterBar filters={groupFilters} columnLabels={groupColumnLabels} onRemoveFilter={handleGroupRemoveFilter} onClearAll={handleGroupClearAllFilters} />
            {renderBulkBar(groupSelectedIds.length, 'group', handleGroupBulkExport, handleGroupBulkDelete, () => setGroupSelectedIds([]))}
            {groupsLoading ? (
              <TableSkeleton />
            ) : (
              <UserGroupsTable
                data={paginatedGroups}
                allData={enrichedGroups}
                visibleColumns={groupVisibleColumns}
                sortColumn={groupSortColumn}
                sortOrder={groupSortOrder}
                onSort={handleGroupSort}
                filters={groupFilters}
                onFilterChange={handleGroupFilterChange}
                selectedIds={groupSelectedIds}
                onSelectRow={handleGroupSelectRow}
                onSelectAll={handleGroupSelectAll}
                onEditGroup={openEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onAddGroup={openAddGroup}
                onClearFilters={handleGroupClearAllFilters}
              />
            )}
            {!groupsLoading && groupTotalFiltered > 0
              ? renderPagination('group', groupShowingStart, groupShowingEnd, groupTotalFiltered, 'groups', groupSafePage, groupTotalPages, groupPageNumbers, setGroupPage, groupPerPage, setGroupPerPage, 'group-per-page')
              : null}
          </>
        )}
      </div>

      {/* ============================================================
          DRAWERS
          ============================================================ */}
      <UserDrawer
        isOpen={userDrawerOpen}
        onClose={() => setUserDrawerOpen(false)}
        user={editingUser}
        onSave={handleSaveUser}
        onDelete={handleDeleteUser}
        onResetPassword={handleResetPassword}
        roles={roleData}
        groups={groupListData}
        myRoleWeight={myRoleWeight}
        myUsername={myUsername}
        displayEditAllowed={displayEditAllowed}
        iAmHigherAuthority={iAmHigherAuth}
        isCorp={isCorp}
      />

      <UserGroupDrawer
        isOpen={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        users={usersData}
        myRoleWeight={myRoleWeight}
        iAmHigherAuthority={iAmHigherAuth}
      />

      {/* Reset Password Dialog */}
      {resetPasswordUser ? (
        <ResetPasswordDialog
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onConfirm={handleResetPassword}
          roleData={roleData}
        />
      ) : null}
    </div>
    </div>
    </>
  );
};

export default UsersPage;
